/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value, tailwindcss/no-unnecessary-arbitrary-value */
'use client';

import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { MaterialIcon } from '../../components/MaterialIcon';
import { ScrollableWithArrows } from '../../components/ScrollableWithArrows';
import type { BaseAttribute } from '../../types/attributes';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { attributeCellText } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { formatOfferedPrice } from '../PlanTierSelector/format';
import { TRANSLATIONS } from './i18n';
import type {
  BenefitAttribute,
  BenefitCategory,
  BenefitComparisonTableProps,
  BenefitComparisonTier,
  BenefitSubAttribute,
} from './types';

// Resolved content of one value cell.
type CellValue =
  | { kind: 'check'; label?: string }
  | { kind: 'text'; text: string }
  | undefined;

// The attribute name join: a product attribute's identity is its
// `systemAttribute.name`. Every `BaseAttribute` variant carries an optional
// `name?: SystemAttribute`, so read it universally — a per-variant switch would
// silently drop the variants it omits (payment/table/multi-text/add-on), making
// those cells resolve as missing.
const getAttributeName = (attribute: BaseAttribute): string =>
  attribute.name?.name ?? '';

const findAttribute = (
  attributes: BaseAttribute[] | undefined,
  attributeKey: string,
): BaseAttribute | undefined =>
  attributes?.find((item) => getAttributeName(item) === attributeKey);

// Resolves a single cell against one tier's product attributes. A boolean
// renders a green check when true; any other attribute renders its formatted
// value. Absent attributes and empty/placeholder values resolve to `undefined`
// so the caller renders the muted dash and drives auto-hide.
const resolveCell = (
  attributes: BaseAttribute[] | undefined,
  attributeKey: string,
): CellValue => {
  const item = findAttribute(attributes, attributeKey);
  if (!item) {
    return undefined;
  }

  if (item.__typename === 'ComponentAttributesAttrBoolean') {
    if (item.value !== true) {
      return undefined;
    }
    const label = item.true_value?.trim();
    return { kind: 'check', label: label ? label : undefined };
  }

  const value = attributeCellText(item);
  return value ? { kind: 'text', text: value } : undefined;
};

const hasValueInAnyTier = (
  tiers: BenefitComparisonTier[],
  attributeKey: string,
): boolean =>
  tiers.some(
    (tier) => resolveCell(tier.product.attributes, attributeKey) !== undefined,
  );

// L3 leaf: shown when explicitly visible and it has data in at least one tier.
const isSubAttributeShown = (
  sub: BenefitSubAttribute,
  tiers: BenefitComparisonTier[],
): boolean =>
  sub.visible !== false && hasValueInAnyTier(tiers, sub.attributeKey);

const shownSubAttributes = (
  attribute: BenefitAttribute,
  tiers: BenefitComparisonTier[],
): BenefitSubAttribute[] =>
  attribute.subChildren.filter((sub) => isSubAttributeShown(sub, tiers));

// L2 row: shown when explicitly visible and either its own key has data in some
// tier or it has at least one shown sub-attribute (so a valued subtree keeps its
// parent).
const isAttributeShown = (
  attribute: BenefitAttribute,
  tiers: BenefitComparisonTier[],
): boolean => {
  if (attribute.visible === false) {
    return false;
  }
  return (
    hasValueInAnyTier(tiers, attribute.attributeKey) ||
    shownSubAttributes(attribute, tiers).length > 0
  );
};

const shownAttributes = (
  category: BenefitCategory,
  tiers: BenefitComparisonTier[],
): BenefitAttribute[] =>
  category.children.filter((attribute) => isAttributeShown(attribute, tiers));

// L1 category: shown when explicitly visible and it has at least one shown L2.
const isCategoryShown = (
  category: BenefitCategory,
  tiers: BenefitComparisonTier[],
): boolean =>
  category.visible !== false && shownAttributes(category, tiers).length > 0;

// Display label: the editor override wins; otherwise the first tier's matching
// product attribute label; last resort the raw key so nothing renders blank.
const resolveDisplayLabel = (
  displayLabelOverride: string | undefined,
  attributeKey: string,
  tiers: BenefitComparisonTier[],
): string => {
  const override = displayLabelOverride?.trim();
  if (override) {
    return override;
  }
  for (const tier of tiers) {
    const item = findAttribute(tier.product.attributes, attributeKey);
    const label = item?.label?.trim() || item?.name?.label?.trim();
    if (label) {
      return label;
    }
  }
  return attributeKey;
};

const ValueCell = ({
  cell,
  isSelected,
  includedLabel,
  isSubRow,
}: {
  cell: CellValue;
  isSelected: boolean;
  includedLabel?: string;
  // L3 (sub-attribute) rows render their values in the `highlights/yellow-dark`
  // accent so the whole detail row reads as one nested unit (CMS-1642 comment
  // 214749 / Figma 14896-2680) — applies on desktop and mobile alike.
  isSubRow?: boolean;
}): JSX.Element => (
  <td
    className={classNames(
      'border-b border-r border-[#e5e7eb] px-spacing/padding/soft-12px py-spacing/padding/y-sm text-center align-middle',
      isSelected ? 'bg-highlights/orange-light' : 'bg-fill/card',
    )}
  >
    {cell === undefined ? (
      // Muted, non-interactive placeholder. A plain span (not the ComparisonTable
      // `NA`, which carries `cursor-pointer` and so reads as clickable) coloured
      // with the `text/disabled` token — the cell is informational, not a control.
      // Sized to match the value cells (12px, no fontSize token) so empty and
      // filled cells align. Kept muted even on L3 rows: a dash is "no data", not
      // an accented value.
      <span className="text-[12px] leading-line-height/table-cell text-text/disabled">
        —
      </span>
    ) : cell.kind === 'check' ? (
      <MaterialIcon
        icon="check"
        aria-label={cell.label ?? includedLabel}
        className="text-highlights/green-dark"
      />
    ) : (
      // L2 values are off-palette per the Figma comp (raw #1a1a1a, not a bound
      // token — matches the approved M3 PlanTierSelector); L3 values take the
      // `highlights/yellow-dark` token. 12px likewise has no fontSize token, so
      // it stays an arbitrary value.
      <span
        className={classNames(
          'text-[12px] leading-line-height/table-cell',
          isSubRow ? 'text-highlights/yellow-dark' : 'text-[#1a1a1a]',
        )}
      >
        {cell.text}
      </span>
    )}
  </td>
);

/**
 * Benefit (TI) comparison table renderer (CMS-1642, Module 4).
 *
 * Pure presentational component: it receives the mapped category/attribute tree
 * plus the active tier set and the page-wide `selectedTier`, and renders a
 * three-level collapsible comparison table. Per-cell values are resolved from
 * each tier's `product.attributes` by `systemAttribute.name` — the same join
 * the TI product comparison table uses. Categories collapse from their
 * `defaultExpanded` state; L2 rows with sub-attributes expand to indented L3
 * rows. Categories, L2 rows and L3 rows with no data in any tier auto-hide, as
 * do items flagged `visible: false`. The selected tier column is highlighted
 * across every row; the label column stays pinned while extra tier columns
 * scroll horizontally.
 */
export const BenefitComparisonTable = ({
  component,
  tiers,
  selectedTier,
  pushDataLayer,
}: BenefitComparisonTableProps): JSX.Element | null => {
  const { title, subtitle, categories } = component;

  // The renderer owns its presentational strings (mirroring M3 PlanTierSelector):
  // the boolean "included" accessible name and the two scroll-affordance labels
  // resolve here from the page's brand/lang context, not threaded from the
  // wrapper.
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const includedLabel = tr('included', staticText);
  const scrollLeftLabel = tr('scrollLeft', staticText);
  const scrollRightLabel = tr('scrollRight', staticText);

  const visibleCategories = useMemo(
    () => categories.filter((category) => isCategoryShown(category, tiers)),
    [categories, tiers],
  );

  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      categories.map((category) => [category.id, !!category.defaultExpanded]),
    ),
  );
  const [expandedAttributes, setExpandedAttributes] = useState<
    Record<string, boolean>
  >({});

  // Keep the expand map in sync when the category set changes after mount (CMS
  // preview / hot reload / async refresh): seed newly-added ids from their
  // `defaultExpanded`, preserve the user's toggles for ids already present, and
  // drop ids no longer in the tree. No-op when nothing changed.
  useEffect(() => {
    setExpandedCategories((prev) => {
      const next: Record<string, boolean> = {};
      for (const category of categories) {
        next[category.id] =
          category.id in prev ? prev[category.id] : !!category.defaultExpanded;
      }
      const sameSize = Object.keys(prev).length === Object.keys(next).length;
      const unchanged =
        sameSize && Object.keys(next).every((id) => prev[id] === next[id]);
      return unchanged ? prev : next;
    });
  }, [categories]);

  const toggleCategory = (id: string) =>
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAttribute = (id: string) =>
    setExpandedAttributes((prev) => ({ ...prev, [id]: !prev[id] }));

  if (tiers.length === 0 || visibleCategories.length === 0) {
    return null;
  }

  // Header column + one column per tier.
  const columnCount = tiers.length + 1;

  const renderValueRow = (
    key: string,
    label: JSX.Element,
    attributeKey: string,
    isSubRow = false,
  ) => (
    <tr key={key}>
      <th
        scope="row"
        className={classNames(
          // Fixed label column, pinned while tier columns scroll: 120px on
          // mobile (CMS-1642 comment 214749 / Figma 14896-2680), 200px from md
          // (CMS-1991). The header cell in <thead> must carry the same widths.
          // Horizontal padding uses the responsive `soft-12px` token; the L3
          // 24px indent is added via `pl-6` below, which overrides the token's
          // left padding.
          // `break-words` keeps long labels (e.g. "Overseas medical expenses")
          // wrapping inside the narrow fixed column instead of overflowing it
          // (matches Figma's word-break:break-word).
          'sticky left-0 z-10 w-[120px] min-w-[120px] break-words border-b border-r border-[#e5e7eb] px-spacing/padding/soft-12px py-spacing/padding/y-sm text-left align-middle font-medium text-font-size/table-cell leading-line-height/table-cell bg-fill/card md:w-[200px] md:min-w-[200px]',
          // L3 rows: yellow-dark label + 24px left indent; L2 rows keep the grey
          // label with the token's own left padding.
          isSubRow ? 'pl-6 text-highlights/yellow-dark' : 'text-[#4a4a4a]',
        )}
      >
        {label}
      </th>
      {tiers.map((tier) => (
        <ValueCell
          key={tier.id}
          cell={resolveCell(tier.product.attributes, attributeKey)}
          isSelected={!!selectedTier && tier.id === selectedTier}
          includedLabel={includedLabel}
          isSubRow={isSubRow}
        />
      ))}
    </tr>
  );

  return (
    <div className="my-6 flex flex-col gap-1">
      {(title || subtitle) && (
        <div className="flex flex-col">
          {title && (
            <div className="font-bold text-font-size/title-1 leading-line-height/title-1 text-[#1a1a1a]">
              {title}
            </div>
          )}
          {subtitle && (
            <div className="font-medium text-font-size/caption leading-line-height/caption text-[#6b7280]">
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* Horizontal overflow + scroll arrows via the shared component (native
          scroll + ResizeObserver, no drag-to-pan). recomputeKey re-checks arrow
          visibility when the column count or expanded row set changes.
          `scrollbar-none` hides the scrollbar chrome (matching the sibling
          ComparisonTable / TableBuilder blocks and the Figma comp) — the table
          still scrolls via swipe/drag and the overlaid arrow buttons. */}
      <ScrollableWithArrows
        className="scrollbar-none overflow-x-auto rounded-[12px] border border-borders/default bg-fill/card"
        recomputeKey={`${tiers.length}:${visibleCategories.length}:${
          Object.values(expandedCategories).filter(Boolean).length
        }:${Object.values(expandedAttributes).filter(Boolean).length}`}
        prevLabel={scrollLeftLabel}
        nextLabel={scrollRightLabel}
      >
        <table className="w-full border-separate border-spacing-0 bg-fill/card">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 w-[120px] min-w-[120px] border-b border-r border-[#e5e7eb] bg-fill/card md:w-[200px] md:min-w-[200px]" />
              {tiers.map((tier) => {
                const isSelected = !!selectedTier && tier.id === selectedTier;
                return (
                  <th
                    key={tier.id}
                    scope="col"
                    className={classNames(
                      'min-w-32 border-b border-r border-[#e5e7eb] px-spacing/padding/soft-12px py-spacing/padding/y-sm text-center',
                      isSelected
                        ? 'bg-highlights/orange-light'
                        : 'bg-fill/card',
                    )}
                  >
                    <div className="flex flex-col items-center gap-[3px]">
                      <span
                        className={classNames(
                          'font-bold text-font-size/table-cell leading-line-height/table-cell',
                          isSelected
                            ? 'text-highlights/orange-dark'
                            : 'text-text/primary',
                        )}
                      >
                        {tier.name}
                      </span>
                      {tier.isPriceLoading ? (
                        // Price still resolving: shimmer pill in place of the
                        // amount, with the period label kept beneath it
                        // (Figma node 13044-48042).
                        <span
                          aria-hidden
                          className="flex h-[21px] w-16 items-center leading-line-height/table-cell-primary"
                        >
                          <span className="h-[15px] w-full animate-pulse rounded-radius/button bg-fill/alternative-3" />
                        </span>
                      ) : (
                        tier.price && (
                          <span className="font-bold text-font-size/table-cell-primary leading-line-height/table-cell-primary text-[#1a1a1a]">
                            {formatOfferedPrice(
                              tier.price,
                              context.currencyDisplay,
                            )}
                          </span>
                        )
                      )}
                      {tier.period && (
                        <span className="text-[10px] leading-line-height/table-cell text-[#6b7280]">
                          {tier.period}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {visibleCategories.map((category, index) => {
              const isExpanded = expandedCategories[category.id];
              const attributes = shownAttributes(category, tiers);
              return (
                <React.Fragment key={category.id}>
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="border-b border-borders/default p-0 bg-[#f3f4f6]"
                    >
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        onClick={() => {
                          pushDataLayer?.({
                            event: 'navigate',
                            component_type: 'accordion',
                            component_title: `Product Comparison Table_${category.comparableLabel}`,
                            action: isExpanded ? 'collapse' : 'expand',
                            index,
                          });
                          toggleCategory(category.id);
                        }}
                        className="flex w-full items-center justify-between py-[9px] text-left"
                      >
                        {/* Pinned to the left edge of the scroll viewport so the
                              category label stays visible while the tier columns
                              scroll horizontally (mirrors TableBuilder's sticky
                              category label). */}
                        <span className="sticky left-0 whitespace-nowrap px-3 font-bold text-[14px] leading-line-height/table-cell text-[#6b7280] bg-[#f3f4f6]">
                          {category.comparableLabel}
                        </span>
                        {/* Chevron pinned to the right edge; both meet at the
                              band's own edges when the table fits (matches Figma). */}
                        <span className="sticky right-0 flex items-center px-3 bg-[#f3f4f6]">
                          <MaterialIcon
                            icon={isExpanded ? 'expand_less' : 'expand_more'}
                            className="text-[#6b7280]"
                          />
                        </span>
                      </button>
                    </td>
                  </tr>

                  {isExpanded &&
                    attributes.map((attribute, attrIndex) => {
                      const subs = shownSubAttributes(attribute, tiers);
                      const hasSubs = subs.length > 0;
                      const isAttrExpanded = !!expandedAttributes[attribute.id];
                      const label = resolveDisplayLabel(
                        attribute.displayLabelOverride,
                        attribute.attributeKey,
                        tiers,
                      );

                      // L2 disclosure chevron sits to the LEFT of the label
                      // (Figma 14896-2680): a tree-disclosure affordance —
                      // `chevron_right` (›) collapsed, `expand_more` (⌄)
                      // expanded — not the right-edge up/down toggle the L1
                      // category bands use.
                      const labelNode = hasSubs ? (
                        <button
                          type="button"
                          aria-expanded={isAttrExpanded}
                          onClick={() => {
                            // Nested title keeps the two accordion levels apart
                            // in reporting; `index` is per category (CMS-1852).
                            pushDataLayer?.({
                              event: 'navigate',
                              component_type: 'accordion',
                              component_title: `Product Comparison Table/${category.comparableLabel}_${label}`,
                              action: isAttrExpanded ? 'collapse' : 'expand',
                              index: attrIndex,
                            });
                            toggleAttribute(attribute.id);
                          }}
                          className="flex w-full items-center gap-spacing/gap/soft-4px text-left"
                        >
                          <MaterialIcon
                            icon={
                              isAttrExpanded ? 'expand_more' : 'chevron_right'
                            }
                            size={2}
                            className="shrink-0 text-[#4a4a4a]"
                          />
                          <span className="min-w-0 flex-1">{label}</span>
                        </button>
                      ) : (
                        <span>{label}</span>
                      );

                      return (
                        <React.Fragment key={attribute.id}>
                          {renderValueRow(
                            attribute.id,
                            labelNode,
                            attribute.attributeKey,
                          )}
                          {isAttrExpanded &&
                            subs.map((sub) =>
                              renderValueRow(
                                sub.id,
                                <span className="block">
                                  {resolveDisplayLabel(
                                    sub.displayLabelOverride,
                                    sub.attributeKey,
                                    tiers,
                                  )}
                                </span>,
                                sub.attributeKey,
                                true,
                              ),
                            )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </ScrollableWithArrows>
    </div>
  );
};
